import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import { TASK_RELATE_TO } from 'constants/formFields';
import {
  DEFAULT_LIMIT,
  DEFAULT_OPTION, DEFAULT_ORDER_BY, TAB_TYPES, TASK_RELATED_TO_FIRST_ITEM,
  TASK_RELATED_TO_FIRST_ITEM_MA,
  TASK_TYPE,
  TASK_TYPE_ACTION,
} from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { useGetMaterialityAssessmentsQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useGetValueChainMapListQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import MaterialityAssessmentTableRows from './MaterialityAssessmentTableRows';
import ValueChainTableRows from './ValueChainTableRows';

import type IMaterialAssessmentItem from 'store/slices/materialAssessment/interfaces/IMaterialAssessmentItem';
import type ITaskItem from '../interfaces/ITaskItem';
import type ITaskRelatedModal from '../interfaces/ITaskRelatedModal';
import type { TTabTypes } from '../interfaces/ITTabTypes';
import type { IVCListRelateTo } from '../interfaces/IVCListRelateTo';

import styles from '../sass/ITaskRelatedModal.module.scss';

function TaskRelatedModal({
  open, onClose, setValueForm, selectedId, typeModal, selectedTab, setSelectedTab,
  setError,
}: ITaskRelatedModal) {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState<ITaskItem | IMaterialAssessmentItem | null>(null);
  const [vcList, setVcList] = useState<IVCListRelateTo[]>([]);
  const [listMA, setListMA] = useState<IMaterialAssessmentItem[]>([]);
  const [vcOffset, setVcOffset] = useState(0);
  const [maOffset, setMaOffset] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const offset = selectedTab === TAB_TYPES.VALUE_CHAIN ? vcOffset : maOffset;

  const {
    data: valueChainMapList, isLoading,
  } = useGetValueChainMapListQuery({
    limit: DEFAULT_LIMIT,
    offset,
  }, {
    skip: selectedTab !== TAB_TYPES.VALUE_CHAIN,
  });

  const { data, isFetching: isMaterialityListLoading } = useGetMaterialityAssessmentsQuery({
    orderBy: DEFAULT_ORDER_BY,
    offset,
  }, {
    skip: selectedTab !== TAB_TYPES.MATERIALITY_ASSESSMENT,
  });
  const { meta, items } = data?.data || {};

  const handleLoadMore = useCallback(() => {
    if (hasNext && !isLoadingMore) {
      setIsLoadingMore(true);
      if (selectedTab === TAB_TYPES.VALUE_CHAIN) {
        setVcOffset((prevOffset) => prevOffset + DEFAULT_LIMIT);
      } else {
        setMaOffset((prevOffset) => prevOffset + DEFAULT_LIMIT);
      }
    }
  }, [hasNext, isLoadingMore, selectedTab]);

  const handleScroll = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    if (!target) return;

    const { scrollTop, scrollHeight, clientHeight } = target;
    const threshold = 50;

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  useEffect(() => {
    if (selectedId && selectedTab === TAB_TYPES.VALUE_CHAIN) {
      setSelectedItem(vcList.find((item) => item.id === selectedId) || null);
    }

    if (typeModal === TASK_TYPE_ACTION.EDIT && selectedTab === TAB_TYPES.VALUE_CHAIN) {
      setSelectedItem(vcList.find((item) => item.id === selectedId) || null);
    }

    if (selectedId && selectedTab === TAB_TYPES.MATERIALITY_ASSESSMENT) {
      setSelectedItem(listMA.find((item) => item.id === selectedId) || null);
    }

    if (typeModal === TASK_TYPE_ACTION.EDIT && selectedTab === TAB_TYPES.MATERIALITY_ASSESSMENT) {
      setSelectedItem(listMA.find((item) => item.id === selectedId) || null);
    }
  }, [selectedId, vcList, typeModal, listMA, selectedTab]);

  useEffect(() => {
    const tbody = tbodyRef.current;
    if (!tbody) return;

    tbody.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      tbody.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (valueChainMapList) {
      const newItems = valueChainMapList?.data?.items?.map((item) => ({
        id: item.id,
        name: item.name,
        created: {
          date: item.createdAt,
          byName: `${item?.createdBy?.firstName} ${item?.createdBy?.lastName}`,
        },
        lastModified: {
          date: item.updatedAt,
          byName: `${item?.updatedBy?.firstName} ${item?.updatedBy?.lastName}`,
        },
        status: item.status,
      })) || [];

      setVcList((prevList) => {
        if (vcOffset === 0) {
          return [TASK_RELATED_TO_FIRST_ITEM, ...newItems];
        }

        const prevIds = new Set(prevList.map((item) => item.id));
        const uniqueNewItems = newItems.filter((item) => !prevIds.has(item.id));
        return [...prevList, ...uniqueNewItems];
      });

      if (selectedTab === TAB_TYPES.VALUE_CHAIN) {
        setHasNext(valueChainMapList.data.meta.hasNext);
      }
      setIsLoadingMore(false);
    }
  }, [valueChainMapList, vcOffset, selectedTab]);

  useEffect(() => {
    if (items !== undefined) {
      if (items.length === 0 && maOffset === 0) {
        setListMA([TASK_RELATED_TO_FIRST_ITEM_MA]);
        return;
      }

      if (items.length > 0) {
        setListMA((prev) => {
          if (maOffset === 0) {
            return [TASK_RELATED_TO_FIRST_ITEM_MA, ...items];
          }

          const prevIds = new Set(prev.map((item) => item.id));
          const newItems = items.filter((item) => !prevIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
    }
    setIsLoadingMore(false);
    if (selectedTab === TAB_TYPES.MATERIALITY_ASSESSMENT) {
      setHasNext(meta?.hasNext || false);
    }
  }, [items, maOffset, meta?.hasNext, selectedTab]);

  useEffect(() => {
    if (items !== undefined) {
      if (items.length === 0 && offset === 0) {
        setListMA([]);
        return;
      }

      if (items.length > 0) {
        setListMA((prev) => {
          const prevIds = new Set(prev.map((item) => item.id));
          const newItems = items.filter((item) => !prevIds.has(item.id));

          if (offset === 0) {
            return [TASK_RELATED_TO_FIRST_ITEM_MA, ...items];
          }

          return [...prev, ...newItems];
        });
      }
    }
    setIsLoadingMore(false);
    setHasNext(meta?.hasNext || false);
  }, [items, offset, meta?.hasNext]);

  useEffect(() => {
    if (selectedTab === TAB_TYPES.VALUE_CHAIN) {
      setHasNext(valueChainMapList?.data?.meta?.hasNext || false);
    } else {
      setHasNext(meta?.hasNext || false);
    }
    setIsLoadingMore(false);
    setSelectedItem(null);
  }, [selectedTab, valueChainMapList?.data?.meta?.hasNext, meta?.hasNext]);

  const handleItemSelect = (item: ITaskItem | IMaterialAssessmentItem): void => {
    setSelectedItem(item);
  };

  const handleConfirm = (): void => {
    if (selectedItem) {
      const title = selectedTab === TAB_TYPES.VALUE_CHAIN
        ? t('valueChainMap.valueChain')
        : t('pillarsAndModules.materialityAssessment');
      setValueForm(TASK_RELATE_TO, {
        label: selectedItem.name.includes(TASK_TYPE.VALUE_CHAIN)
        || selectedItem.name.includes(TASK_TYPE.MATERIALITY_ASSESSMENT)
          ? selectedItem.name
          : `${title} - ${selectedItem.name}`,
        value: `${selectedItem.id}`,
      });
      setError(TASK_RELATE_TO, {
        message: '',
      });
      onClose();
    }
  };

  const onCloseModal = (): void => {
    setSelectedItem(null);
    onClose();
    if (typeModal !== TASK_TYPE_ACTION.EDIT) {
      setValueForm(TASK_RELATE_TO, DEFAULT_OPTION);
    }
    setVcOffset(0);
    setMaOffset(0);
    setHasNext(false);
    setIsLoadingMore(false);
  };

  const handleSelectTab = (tab: TTabTypes) => {
    setSelectedTab(tab);
  };

  return (
    <Modal
      open={open}
      isButtonsVisible={false}
      className={styles.modal}
      title={t('taskManagement.taskRelatedTo')}
      description={t('taskManagement.taskRelatedToDescription')}
    >
      <div className={styles.content}>
        <div className={styles.tabsWrapper}>
          <div className={clsx(styles.tab, { [styles.active]: selectedTab === TAB_TYPES.VALUE_CHAIN })}>
            <Button
              title={t('valueChainMap.valueChain')}
              variant={BUTTON_VARIANT.ghost}
              onClick={() => handleSelectTab(TAB_TYPES.VALUE_CHAIN)}
              className={clsx(styles.button, { [styles.active]: selectedTab === TAB_TYPES.VALUE_CHAIN })}
            />
          </div>
          <div className={clsx(styles.tab, {
            [styles.active]: selectedTab === TAB_TYPES.MATERIALITY_ASSESSMENT,
          })}
          >
            <Button
              title={t('pillarsAndModules.materialityAssessment')}
              variant={BUTTON_VARIANT.ghost}
              onClick={() => handleSelectTab(TAB_TYPES.MATERIALITY_ASSESSMENT)}
              className={clsx(styles.button, { [styles.active]: selectedTab === TAB_TYPES.MATERIALITY_ASSESSMENT })}
            />
          </div>
        </div>
        <div className={styles.tabContent}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('common.created')}</th>
                <th>{t('common.lastModified')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody ref={tbodyRef} className={styles.tbody}>
              {(isLoading || isMaterialityListLoading) && offset === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.loaderCell}>
                    <Loader isPageLoading={isLoading || isMaterialityListLoading} />
                  </td>
                </tr>
              ) : null}
              {selectedTab === TAB_TYPES.VALUE_CHAIN
              && !isLoading && !isMaterialityListLoading
                ? (
                  <ValueChainTableRows
                    vcList={vcList}
                    selectedItemId={selectedItem?.id}
                    onItemSelect={handleItemSelect}
                    styles={styles}
                  />
                ) : null}
              {selectedTab === TAB_TYPES.MATERIALITY_ASSESSMENT
              && !isLoading && !isMaterialityListLoading
                ? (
                  <MaterialityAssessmentTableRows
                    listMA={listMA}
                    selectedItemId={selectedItem?.id}
                    onItemSelect={handleItemSelect}
                    styles={styles}
                  />
                ) : null}

              {isLoadingMore ? (
                <tr>
                  <td colSpan={4} className={styles.loadingMoreCell}>
                    <div className={styles.loadingMore}>
                      {t('common.loading')}
                      ...
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          title={t('common.cancel')}
          variant={BUTTON_VARIANT.cancel}
          className={styles.button}
          onClick={onCloseModal}
        />
        <Button
          title={t('common.confirm')}
          variant={BUTTON_VARIANT.primary}
          className={styles.button}
          onClick={handleConfirm}
          disabled={!selectedItem}
        />
      </div>
    </Modal>
  );
}

export default TaskRelatedModal;
